import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api, ApiError } from '@/services/api';
import { Test, Task, Evaluation, TaskResult } from '@/types';
import PageLayout from '@/shared/ui/layout/PageLayout';
import ModelNameInput from '@/shared/ui/inputs/ModelNameInput';
import LoadingSpinner from '@/shared/ui/feedback/LoadingSpinner';
import ErrorMessage from '@/shared/ui/feedback/ErrorMessage';
import { GroupedContent, DetailSection } from '@/shared/ui/layout';
import BinaryScoreInput from '@/shared/ui/inputs/BinaryScoreInput';
import { FaRegCopy } from 'react-icons/fa';
import './styles.css';
import { PREDEFINED_COLORS } from '@/shared/constants/colors';

const EvaluationForm: React.FC = () => {
  const { testId, evaluationId } = useParams<{ testId: string; evaluationId: string }>();
  const navigate = useNavigate();
  
  const [test, setTest] = useState<Test | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [modelName, setModelName] = useState('');
  const [color, setColor] = useState<string>('');
  const [results, setResults] = useState<Record<string, TaskResult>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCorrection, setIsCorrection] = useState(false);

  // Group tasks by category
  const groupedTasks = React.useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    tasks.forEach((task: Task) => {
      const category = task.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(task);
    });
    return Object.keys(grouped)
      .sort()
      .reduce((acc: Record<string, Task[]>, category: string) => {
        acc[category] = grouped[category];
        return acc;
      }, {} as Record<string, Task[]>);
  }, [tasks]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!testId) return;
        
        // Fetch test details
        const testData = await api.tests.getById(testId);
        setTest(testData);

        // Fetch full task details
        const taskIds = testData.tasks.map((t: string | Task) => 
          typeof t === 'string' ? t : t._id
        );
        const taskDetails = await Promise.all(
          taskIds.filter((id): id is string => id !== undefined)
            .map(taskId => api.tasks.getById(taskId))
        );
        setTasks(taskDetails);

        // If evaluationId exists, fetch the evaluation data
        if (evaluationId) {
          const evaluationData = await api.evaluations.getById(evaluationId);
          setModelName(evaluationData.modelName);
          setColor(evaluationData.color || PREDEFINED_COLORS[0]);
          setIsCorrection(evaluationData.status === 'completed');
          
          // Convert evaluation results to the required format
          const existingResults: Record<string, TaskResult> = {};
          evaluationData.results.forEach((result: TaskResult) => {
            const taskId = typeof result.task === 'string' ? result.task : result.task._id;
            if (taskId) {
              existingResults[taskId] = {
                task: taskId,
                score: result.score,
                notes: result.notes || ''
              };
            }
          });
          setResults(existingResults);
        } else {
          // Initialize results object with null scores for new evaluation
          setColor(PREDEFINED_COLORS[0]);
          const initialResults: Record<string, TaskResult> = {};
          taskDetails.forEach(task => {
            if (task._id) {
              initialResults[task._id] = {
                task: task._id,
                score: null,
                notes: ''
              };
            }
          });
          setResults(initialResults);
        }
        
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [testId, evaluationId]);

  const handleScoreChange = (taskId: string, value: number | boolean | null) => {
    setResults((prev: Record<string, TaskResult>) => ({
      ...prev,
      [taskId]: { ...prev[taskId], score: value }
    }));
  };

  const handleNotesChange = (taskId: string, notes: string) => {
    setResults((prev: Record<string, TaskResult>) => ({
      ...prev,
      [taskId]: { ...prev[taskId], notes }
    }));
  };

  const handleCopyContent = (content: string) => {
    navigator.clipboard.writeText(content)
      .then(() => {
        toast.success('Content copied to clipboard');
      })
      .catch(err => {
        toast.error('Failed to copy content');
      });
  };

  const saveEvaluation = async (status: 'in-progress' | 'completed') => {
    if (!test || !testId) {
      toast.error('Test information is missing');
      return;
    }
    
    // Validate model name is provided
    if (!modelName.trim()) {
      toast.error('Model name is required');
      return;
    }

    // For completed status, check if all tasks have been evaluated
    if (status === 'completed') {
      const hasIncomplete = hasUnevaluatedTasks();
      if (hasIncomplete) {
        toast.error('All tasks must be evaluated before completing');
        return;
      }
    }

    try {
      setSaving(true);
      
      // Validate and prepare results
      const validResults = Object.entries(results).map(([taskId, result]) => ({
        task: taskId,
        score: result.score,
        notes: result.notes || ''
      }));

      // Additional validation for completed status - no null scores allowed
      if (status === 'completed') {
        const hasNullScores = validResults.some(result => result.score === null);
        if (hasNullScores) {
          toast.error('All tasks must be evaluated before completing');
          return;
        }
      }

      const evaluationData: Omit<Evaluation, '_id' | 'createdAt' | 'completedAt'> = {
        modelName: modelName.trim(),
        test: testId,
        results: validResults,
        status,
        color,
      };
      
      if (evaluationId) {
        // Update existing evaluation
        await api.evaluations.update(evaluationId, evaluationData);
      } else {
        // Create new evaluation
        await api.evaluations.create(evaluationData);
      }
      
      // Determine the success message based on the action
      let message = '';
      if (status === 'completed') {
        message = isCorrection ? 'Evaluation corrected successfully!' : 'Evaluation completed successfully!';
      } else { // status === 'in-progress'
        message = 'Progress saved successfully!';
      }
      
      toast.success(message);
      navigate('/evaluations');
    } catch (err) {
      const errorMessage = (err as ApiError).message || 'Failed to save evaluation.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveEvaluation('completed');
  };

  const handleSaveProgress = async () => {
    await saveEvaluation('in-progress');
  };

  // Add this helper function to check if all tasks are evaluated
  const hasUnevaluatedTasks = () => {
    return Object.values(results).some(result => result.score === null);
  };

  if (loading) return <LoadingSpinner message="Loading test details..." />;
  if (!test) return <ErrorMessage message="Test not found" />;

  const renderSubmitButton = () => {
    const isFormIncomplete = hasUnevaluatedTasks();
    const isModelNameEmpty = !modelName.trim();
    const isDisabled = saving || isModelNameEmpty || isFormIncomplete;

    return (
      <div className="tooltip-container" data-position="bottom">
        <button 
          type="submit" 
          className="button primary" 
          disabled={isDisabled}
          form="evaluation-form"
        >
          {saving ? 'Saving...' : (isCorrection ? 'Update' : 'Complete')}
        </button>
        {isModelNameEmpty && (
          <div className="tooltip">
            Model name required
          </div>
        )}
        {!isModelNameEmpty && isFormIncomplete && (
          <div className="tooltip">
            All tasks must be evaluated before completing
          </div>
        )}
      </div>
    );
  };

  const renderSaveButton = () => (
    <div className="tooltip-container" data-position="bottom">
      <button 
        type="button" 
        onClick={handleSaveProgress}
        className="button secondary"
        disabled={saving || !modelName.trim()}
      >
        {saving ? 'Saving...' : (isCorrection ? 'Save Changes' : 'Save Progress')}
      </button>
      {!modelName.trim() && (
        <div className="tooltip">
          Model name required
        </div>
      )}
    </div>
  );

  const actions = (
    <div className="actions-group">
      {renderSaveButton()}
      {renderSubmitButton()}
    </div>
  );

  const detailItems = [
    {
      label: 'Model Name:',
      value: (
        <div className="model-input-wrapper">
          <ModelNameInput
            value={modelName}
            onChange={setModelName}
            required={false}
            label=""
            className="model-name-input"
            placeholder="Enter model name (required)"
          />
        </div>
      )
    },
    {
      label: 'Chart Color:',
      value: (
        <div className="color-selector-wrapper">
          {PREDEFINED_COLORS.map((c) => (
            <div
              key={c}
              className={`color-swatch ${color === c ? 'selected' : ''}`}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>
      ),
    },
    {
      label: 'Description:',
      value: test.description
    }
  ];

  // Render function for task groups
  const renderTaskGroup = (category: string, categoryTasks: Task[]) => (
    <div className="results-list">
      {categoryTasks.map((task: Task) => (
        <div 
          key={task._id} 
          className={`task-evaluation-card ${results[task._id!]?.score === null ? 'not-evaluated' : ''}`}
        >
          <h3 className="task-title">{task.title}</h3>
          
          <div className="task-content">
            <div className="content-box">
              <div className="text-content">
                {task.content}
              </div>
              <button 
                className="copy-button" 
                onClick={() => handleCopyContent(task.content)}
                title="Copy to clipboard"
                type="button"
              >
                <FaRegCopy size={16} />
              </button>
            </div>
            
            {/* Display task attachments */}
            {task.attachments && task.attachments.length > 0 && (
              <div className="task-attachments">
                <div className="attachments-list">
                  {task.attachments.map((attachment) => {
                    const isImage = attachment.mimetype.startsWith('image/');
                    const isPdf = attachment.mimetype === 'application/pdf';
                    const downloadUrl = api.tasks.getAttachmentUrl(task._id!, attachment._id!);
                    
                    // Function to handle drag start
                    const handleDragStart = (e: React.DragEvent) => {
                      // Set the drag data with the download URL and filename
                      e.dataTransfer.setData('text/uri-list', downloadUrl);
                      e.dataTransfer.setData('text/plain', downloadUrl);
                      e.dataTransfer.setData('DownloadURL', `${attachment.mimetype}:${attachment.originalname}:${downloadUrl}`);
                      // Set the drag effect to 'copy' to indicate a file copy operation
                      e.dataTransfer.effectAllowed = 'copy';
                    };
                    
                    return (
                      <div 
                        key={attachment._id} 
                        className="attachment-item"
                        draggable="true"
                        onDragStart={handleDragStart}
                        title="Drag to desktop or other applications"
                      >
                        {isImage ? (
                          <div className="image-preview">
                            <img src={downloadUrl} alt={attachment.originalname} />
                          </div>
                        ) : (
                          <div className="file-info">
                            <span className="file-name">{isPdf ? '📄 PDF: ' : ''}{attachment.originalname}</span>
                          </div>
                        )}
                        <div className="attachment-actions">
                          <a 
                            href={downloadUrl} 
                            download={attachment.originalname}
                            className="download-link"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {task.scoringMethod === 'numerical' && (
            <div className="scoring-rules">
              <div className="content-box">
                {task.scoringRules}
              </div>
            </div>
          )}

          <div className="solution-score-container">
            <div className="solution-section">
              <div className="content-box">
                {task.solution}
              </div>
            </div>

            <div className="score-section">
              <div className="score-display">
                <label>Score:</label>
                {task.scoringMethod === 'numerical' ? (
                  <div className="numerical-input">
                    <input
                      type="number"
                      value={results[task._id!]?.score === null ? '' : results[task._id!]?.score as number}
                      min={task.scoringScale?.min}
                      max={task.scoringScale?.max}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        handleScoreChange(task._id!, value);
                      }}
                      placeholder="Not evaluated"
                    />
                    <span className="scale-info">
                      (Scale: {task.scoringScale?.min} - {task.scoringScale?.max})
                    </span>
                  </div>
                ) : (
                  <BinaryScoreInput
                    value={results[task._id!]?.score as boolean | null}
                    onChange={(value) => handleScoreChange(task._id!, value)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="notes-section">
            <textarea
              value={results[task._id!]?.notes || ''}
              onChange={(e) => handleNotesChange(task._id!, e.target.value)}
              placeholder="Add notes about the model's performance..."
            />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <PageLayout
      title={isCorrection ? `Correct Evaluation: ${test.name}` : `Evaluate Test: ${test.name}`}
      actions={actions}
    >
      <form id="evaluation-form" onSubmit={handleSubmit} className="evaluation-content">
        <DetailSection items={detailItems} />

        <div className="tasks-container">
          <h3>Tasks by Category</h3>
          <GroupedContent
            groups={groupedTasks}
            renderGroup={renderTaskGroup}
            emptyMessage="No tasks found in this test."
            className="evaluation-grouped-content"
          />
        </div>

        <div className="bottom-actions">
          <div className="separator"></div>
          <div className="bottom-submit">
            {actions}
          </div>
        </div>
      </form>
    </PageLayout>
  );
};

export default EvaluationForm; 